import { useState, useEffect, useMemo } from 'react';
import { getCalibrationFieldConfig } from '../../sensors/sensorSchemas';
import { CalibrationInputFieldConfig, CalibrationPointsConfig } from '../../sensors/types';
import { initializeCalibrationDataFields } from '../utils/dataTransformation';

const DEFAULT_CALIBRATION_POINTS: CalibrationPointsConfig = {
  increasing: [10, 20, 30, 40, 50],
  decreasing: [40, 30, 20, 10, 0],
  secondIncreasing: [10, 20, 30, 40, 50]
};

/**
 * Calibration points scale with the sensor's measurement range (step =
 * range / 5), exactly matching each schema's generateTemplateLabels — e.g. a
 * 1 MPa piezometer calibrates at 0.2/0.4/0.6/0.8/1, a 0.7 MPa one at
 * 0.14/0.28/0.42/0.56/0.7. Falls back to the schema's static default when the
 * range can't be parsed (nothing selected yet) or the schema doesn't scale
 * this way.
 */
const calibrationPointsFromRange = (range: string | undefined): CalibrationPointsConfig | null => {
  if (!range) return null;
  const match = range.match(/(\d+\.?\d*)/);
  if (!match) return null;
  const rangeValue = parseFloat(match[1]);
  if (isNaN(rangeValue) || rangeValue <= 0) return null;

  const step = rangeValue / 5;
  const roundPoint = (n: number) => Math.round(n * 1000) / 1000;

  return {
    increasing: [1, 2, 3, 4, 5].map((i) => roundPoint(i * step)),
    decreasing: [4, 3, 2, 1, 0].map((i) => roundPoint(i * step)),
    secondIncreasing: [1, 2, 3, 4, 5].map((i) => roundPoint(i * step)),
  };
};

export interface UseCalibrationFormReturn {
  individualCalibrationData: any;
  setIndividualCalibrationData: React.Dispatch<React.SetStateAction<any>>;
  calibrationFieldConfig: CalibrationInputFieldConfig | null;
  setCalibrationFieldConfig: React.Dispatch<React.SetStateAction<CalibrationInputFieldConfig | null>>;
  schemaConfig: {
    fieldConfig: CalibrationInputFieldConfig | null;
    calibrationPoints: CalibrationPointsConfig;
    generatePlaceholder: (point: number, sequence?: 'inc' | 'dec' | 'inc2') => string;
  };
  clearAllFields: () => void;
}

export interface SensorInfoSeed {
  sensorId: string;
  sensorType: string;
  category: string;
  modelFamily: string;
  model: string;
  range: string;
}

export const useCalibrationForm = (
  prefillSensorInfo?: SensorInfoSeed | null,
  prefillInputData?: any,
  isOpen?: boolean
) => {
  // Lazy initializer: seeded synchronously from props on the very first
  // render, so Step 1's own dropdown-cascade effects (sensor type -> category
  // -> model family -> model -> range) never see a moment where the fields
  // are wrong and never race with SensorInfoStep's auto-sync effect.
  const [individualCalibrationData, setIndividualCalibrationData] = useState(() => {
    const base = initializeCalibrationDataFields();
    return prefillSensorInfo ? { ...base, ...prefillSensorInfo } : base;
  });
  const [calibrationFieldConfig, setCalibrationFieldConfig] = useState<CalibrationInputFieldConfig | null>(null);

  // Schema configuration hook - consolidates all schema-related logic
  const schemaConfig = useMemo(() => {
    const fieldConfig = getCalibrationFieldConfig(
      individualCalibrationData.sensorType || null,
      individualCalibrationData.category || undefined
    );

    // Points scale with the selected range (step = range/5) for schemas that
    // support it (piezometer, displacement) — same math as their own
    // generateTemplateLabels, so points/placeholders/labels always agree.
    // Falls back to the schema's static points when no range is selected yet
    // or the schema doesn't scale this way.
    const rangeBasedPoints = fieldConfig?.generateTemplateLabels
      ? calibrationPointsFromRange(individualCalibrationData.range)
      : null;
    const calibrationPoints = rangeBasedPoints || fieldConfig?.calibrationPoints || DEFAULT_CALIBRATION_POINTS;

    const generatePlaceholder = fieldConfig?.generatePlaceholder
      ? ((point: number, sequence?: 'inc' | 'dec' | 'inc2') => {
          // Wrap the original function to make sequence optional
          // The original function requires sequence, so we provide a default
          return fieldConfig.generatePlaceholder!(point, sequence || 'inc');
        })
      : ((point: number, sequence?: 'inc' | 'dec' | 'inc2') => `${1800 + point * 5}`);

    return {
      fieldConfig,
      calibrationPoints,
      generatePlaceholder,
    };
  }, [individualCalibrationData.sensorType, individualCalibrationData.category, individualCalibrationData.range]);

  // Pre-fill Part-1 additional fields from the previous calibration's
  // input_data (e.g. equipment/DWT traceable certificate number). BOL,
  // AOL, raw_frequency, and temperature_frequency are intentionally NOT
  // pre-filled here — those are Step-2 readings and must be entered fresh
  // for every calibration, including a recalibration.
  useEffect(() => {
    if (isOpen && prefillInputData) {
      const certificateNo =
        prefillInputData.equipment_calibration_certificate_no ??
        prefillInputData.dwt_traceable_certificate_no;
      if (certificateNo !== undefined && certificateNo !== null) {
        setIndividualCalibrationData((prev: any) => ({
          ...prev,
          dwtTraceable: certificateNo.toString(),
        }));
      }
    }
  }, [isOpen, prefillInputData]);

  const clearAllFields = () => {
    const defaultPoints = schemaConfig.calibrationPoints;
    const currentData = individualCalibrationData;
    const allCalibrationFields: Record<string, string> = {};
    
    // Find all calibration point fields in current data and mark them for clearing
    Object.keys(currentData).forEach(key => {
      if (key.startsWith('point_') || key === 'point_0') {
        allCalibrationFields[key] = "";
      }
    });
    
    const clearedData = initializeCalibrationDataFields(defaultPoints);
    const finalClearedData = {
      ...clearedData,
      ...allCalibrationFields
    };
    
    setIndividualCalibrationData(finalClearedData);
    
    if (calibrationFieldConfig) {
      setCalibrationFieldConfig({
        ...calibrationFieldConfig,
        calibrationPoints: defaultPoints
      });
    }
  };

  return {
    individualCalibrationData,
    setIndividualCalibrationData,
    calibrationFieldConfig,
    setCalibrationFieldConfig,
    schemaConfig,
    clearAllFields,
  };
};

